<template>
  <div 
    ref="containerRef"
    :class="splitViewClass"
    @mousemove="onMouseMove"
    @mouseup="onMouseUp"
    @mouseleave="onMouseUp"
  >
    <div 
      :class="primaryPaneClass"
      :style="primaryPaneStyle"
    >
      <slot name="primary" />
    </div>
    
    <div 
      v-if="!collapsed"
      ref="resizerRef"
      :class="resizerClass"
      @mousedown="onMouseDown"
    >
      <div class="resizer-line" />
    </div>
    
    <div 
      :class="secondaryPaneClass"
      :style="secondaryPaneStyle"
    >
      <slot name="secondary" />
    </div>
    
    <!-- Collapse/Expand Toggle -->
    <!-- <Button
      v-if="collapsible"
      :class="toggleButtonClass"
      variant="icon"
      :icon="collapsed ? expandIcon : collapseIcon"
      size="small"
      :title="collapsed ? 'Expand sidebar' : 'Collapse sidebar'"
      @click="toggleCollapse"
    /> -->
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted } from 'vue';

export interface SplitViewProps {
  orientation?: 'horizontal' | 'vertical';
  primarySize?: number;
  minPrimarySize?: number;
  maxPrimarySize?: number;
  collapsible?: boolean;
  collapsed?: boolean;
  primaryPane?: 'first' | 'second';
  resizable?: boolean;
}

const props = withDefaults(defineProps<SplitViewProps>(), {
  orientation: 'horizontal',
  primarySize: 300,
  minPrimarySize: 100,
  maxPrimarySize: 800,
  collapsible: true,
  collapsed: false,
  primaryPane: 'first',
  resizable: true,
});

const emit = defineEmits<{
  'update:primarySize': [size: number];
  'update:collapsed': [collapsed: boolean];
  'resize': [size: number];
  'collapse': [collapsed: boolean];
}>();

const containerRef = ref<HTMLElement>();
const resizerRef = ref<HTMLElement>();
const isDragging = ref(false);
const currentSize = ref(props.primarySize);
const dragStartPosition = ref(0);
const dragStartSize = ref(0);

const isHorizontal = computed(() => props.orientation === 'horizontal');
const collapsed = computed(() => props.collapsed);

const splitViewClass = computed(() => [
  'split-view',
  `split-view--${props.orientation}`,
  {
    'split-view--collapsed': collapsed.value,
    'split-view--dragging': isDragging.value,
  }
]);

const primaryPaneClass = computed(() => [
  'split-pane',
  'split-pane--primary',
  `split-pane--${props.primaryPane === 'first' ? 'first' : 'second'}`
]);

const secondaryPaneClass = computed(() => [
  'split-pane',
  'split-pane--secondary',
  `split-pane--${props.primaryPane === 'first' ? 'second' : 'first'}`
]);

const resizerClass = computed(() => [
  'split-resizer',
  `split-resizer--${props.orientation}`,
  {
    'split-resizer--active': isDragging.value,
    'split-resizer--disabled': !props.resizable,
  }
]);

// toggle button feature currently commented out in template; remove computed to avoid unused var error

const primaryPaneStyle = computed(() => {
  if (collapsed.value) {
    return {
      [isHorizontal.value ? 'width' : 'height']: '0px',
      overflow: 'hidden',
    };
  }
  
  return {
    [isHorizontal.value ? 'width' : 'height']: `${currentSize.value}px`,
    [isHorizontal.value ? 'minWidth' : 'minHeight']: `${props.minPrimarySize}px`,
    [isHorizontal.value ? 'maxWidth' : 'maxHeight']: `${props.maxPrimarySize}px`,
  };
});

const secondaryPaneStyle = computed(() => {
  const remainingSize = collapsed.value ? 
    '100%' : 
    `calc(100% - ${currentSize.value + (props.resizable ? 4 : 0)}px)`;
  
  return {
    [isHorizontal.value ? 'width' : 'height']: remainingSize,
  };
});

// Removed expand/collapse icon computeds (not used while toggle button is commented out)

const onMouseDown = (event: MouseEvent) => {
  if (!props.resizable) return;
  
  event.preventDefault();
  isDragging.value = true;
  dragStartPosition.value = isHorizontal.value ? event.clientX : event.clientY;
  dragStartSize.value = currentSize.value;
  
  document.body.style.cursor = isHorizontal.value ? 'col-resize' : 'row-resize';
  document.body.style.userSelect = 'none';
};

const onMouseMove = (event: MouseEvent) => {
  if (!isDragging.value) return;
  
  event.preventDefault();
  const currentPosition = isHorizontal.value ? event.clientX : event.clientY;
  const delta = currentPosition - dragStartPosition.value;
  let newSize = dragStartSize.value + delta;
  
  // Respect min/max constraints
  newSize = Math.max(props.minPrimarySize, Math.min(props.maxPrimarySize, newSize));
  
  currentSize.value = newSize;
  emit('update:primarySize', newSize);
  emit('resize', newSize);
};

const onMouseUp = () => {
  if (!isDragging.value) return;
  
  isDragging.value = false;
  document.body.style.cursor = '';
  document.body.style.userSelect = '';
};

// Removed toggleCollapse handler (toggle UI disabled)

// Handle window resize
const handleWindowResize = () => {
  if (!containerRef.value) return;
  
  const containerSize = isHorizontal.value ? 
    containerRef.value.clientWidth : 
    containerRef.value.clientHeight;
  
  const maxAllowedSize = containerSize * 0.8; // Max 80% of container
  
  if (currentSize.value > maxAllowedSize) {
    currentSize.value = Math.max(props.minPrimarySize, maxAllowedSize);
    emit('update:primarySize', currentSize.value);
  }
};

onMounted(() => {
  window.addEventListener('resize', handleWindowResize);
  currentSize.value = props.primarySize;
});

onUnmounted(() => {
  window.removeEventListener('resize', handleWindowResize);
  onMouseUp(); // Clean up any ongoing drag
});

// Watch for external primarySize changes
watch(() => props.primarySize, (newSize) => {
  currentSize.value = newSize;
});
</script>

<style scoped lang="scss">
.split-view {
  display: flex;
  width: 100%;
  height: 100%;
  position: relative;
  background-color: var(--vscode-background);
  
  &--horizontal {
    flex-direction: row;
  }
  
  &--vertical {
    flex-direction: column;
  }
  
  &--dragging {
    user-select: none;
  }
}

.split-pane {
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background-color: var(--vscode-background);

  &--first {
    order: 0;
    // width: max-content !important;
  }
  
  &--second {
    order: 2;
  }
}

.split-resizer {
  position: relative;
  background-color: var(--vscode-border);
  transition: background-color 0.1s ease;
  order: 1;
  
  &--horizontal {
    width: 4px;
    cursor: col-resize;
    
    &:hover,
    &--active {
      background-color: var(--vscode-sash-hoverBorder);
    }
  }
  
  &--vertical {
    height: 4px;
    cursor: row-resize;
    
    &:hover,
    &--active {
      background-color: var(--vscode-sash-hoverBorder);
    }
  }
  
  &--disabled {
    cursor: default !important;
    
    &:hover {
      background-color: var(--vscode-border) !important;
    }
  }
}

.resizer-line {
  position: absolute;
  background-color: transparent;
  
  .split-resizer--horizontal & {
    top: 0;
    bottom: 0;
    left: 50%;
    width: 1px;
    transform: translateX(-50%);
  }
  
  .split-resizer--vertical & {
    left: 0;
    right: 0;
    top: 50%;
    height: 1px;
    transform: translateY(-50%);
  }
  
  .split-resizer:hover &,
  .split-resizer--active & {
    background-color: var(--vscode-sash-hoverBorder);
  }
}

.split-toggle {
  position: absolute;
  z-index: 10;
  
  &--horizontal {
    top: var(--spacing-md);
    
    &.split-toggle--collapsed {
      left: var(--spacing-sm);
    }
    
    &.split-toggle--expanded {
      left: calc(v-bind(currentSize) * 1px - 28px);
    }
  }
  
  &--vertical {
    left: var(--spacing-md);
    
    &.split-toggle--collapsed {
      top: var(--spacing-sm);
    }
    
    &.split-toggle--expanded {
      top: calc(v-bind(currentSize) * 1px - 28px);
    }
  }
}

// Responsive behavior
@media (max-width: 768px) {
  .split-view--horizontal {
    flex-direction: column;
    
    .split-resizer--horizontal {
      width: auto;
      height: 4px;
      cursor: row-resize;
    }
    
    .split-toggle--horizontal {
      top: calc(v-bind(currentSize) * 1px - 14px);
      left: 50%;
      transform: translateX(-50%);
      
      &.split-toggle--collapsed {
        top: var(--spacing-sm);
        left: 50%;
        transform: translateX(-50%);
      }
    }
  }
}
</style>
